import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import type { FiltersAndSorting } from '../requests-overview-content/requests-overview-content.component';

@Component({
  selector: 'fi-requests-overview-filter-chips',
  templateUrl: './requests-overview-filter-chips.component.html',
  styleUrls: ['./requests-overview-filter-chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestsOverviewFilterChipsComponent {
  @Input() filters: FiltersAndSorting;
  @Input() isMobile = false;

  @Output() filterRemoved = new EventEmitter<{ filters: any[]; index: number }>();
  @Output() allFiltersRemoved = new EventEmitter<void>();
  @Output() sortBadgeClicked = new EventEmitter<void>();
  @Output() dateFilterCleared = new EventEmitter<void>();

  trackByIndex = (index: number) => index;

  get hasFilters(): boolean {
    return !!(
      this.filters?.leadTypes?.length ||
      this.filters?.contactTypes?.length ||
      this.filters?.leadStatuses?.length ||
      this.filters?.countries?.length ||
      this.filters?.deadlineStatus?.length ||
      (this.filters?.fromDate && this.filters?.toDate) ||
      this.filters?.premiumProjectCustomerTypes?.length ||
      this.filters?.commissionSearches?.length ||
      this.filters?.contactSearches?.length ||
      this.filters?.responsibleSearches?.length
    );
  }

  get showMobileSort(): boolean {
    return !!(this.isMobile && this.filters?.sortType);
  }

  get sortLabelKey(): string {
    const directionKey = this.filters?.sortDirection === 'DESC'
      ? 'REQUEST_OVERVIEW_FILTER_SORT_DESCENDING'
      : 'REQUEST_OVERVIEW_FILTER_SORT_ASCENDING';
    const fieldKey = this.getSortFieldLabelKey();

    return `%REQUEST_OVERVIEW_FILTER_SORT_BY%: %${directionKey}% · %${fieldKey}%`;
  }

  remove(filters: any[], index: number): void {
    this.filterRemoved.emit({ filters, index });
  }

  clearDateFilter(event: Event): void {
    event.stopPropagation();
    this.dateFilterCleared.emit();
  }

  get hasDateFilter(): boolean {
    return !!(this.filters?.fromDate && this.filters?.toDate);
  }

  get dateRangeLabel(): string {
    if (!this.hasDateFilter) {
      return '';
    }

    return [this.filters.fromDate, this.filters.toDate]
      .map(date => this.formatDateWithYear(date))
      .join(' - ');
  }

  private formatDateWithYear(date: Date): string {
    const normalizedDate = new Date(date);
    const day = `${normalizedDate.getDate()}`.padStart(2, '0');
    const month = `${normalizedDate.getMonth() + 1}`.padStart(2, '0');
    const year = normalizedDate.getFullYear();

    return `${day}.${month}.${year}`;
  }

  private getSortFieldLabelKey(): string {
    switch ( this.filters?.sortType ) {
      case 'TYPE':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_LEAD_TYPE';
      case 'COMMISSION':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_PROJECT';
      case 'CONTACT':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_CONTACT';
      case 'DATE2':
        return 'REQUEST_OVERVIEW_FILTER_CREATED_ON';
      case 'LAND':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_COUNTRY';
      case 'STATUS':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_STATUS';
      case 'RESPONSIBLE':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_RESPONSIBLE';
      case 'DATE':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_DUE_DATE';
      case 'LAST_ACTION':
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_LAST_ACTION';
      default:
        return 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_DUE_DATE';
    }
  }
}
